import { useState } from "react";
import { arvoresProximas, tiposManejo } from "@/mocks/coletor";

interface MobileInspectionFormProps {
  onClose: () => void;
}

export default function MobileInspectionForm({ onClose }: MobileInspectionFormProps) {
  const [passo, setPasso] = useState(0);
  const [busca, setBusca] = useState("");
  const [arvoreSelecionada, setArvoreSelecionada] = useState<typeof arvoresProximas[0] | null>(null);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [fitossanitario, setFitossanitario] = useState("");
  const [risco, setRisco] = useState("");
  const [manejo, setManejo] = useState("");
  const [medidas, setMedidas] = useState({ dap: "", altura: "" });

  const passos = ["Buscar árvore", "Confirmar", "Medidas", "Fitossanidade", "Risco", "Manejo", "Salvar"];

  const arvoresFiltradas = busca.length > 1
    ? arvoresProximas.filter((a) =>
        a.id.toLowerCase().includes(busca.toLowerCase()) ||
        a.especie.toLowerCase().includes(busca.toLowerCase()) ||
        a.bairro.toLowerCase().includes(busca.toLowerCase())
      )
    : [];

  const estadosFitossanitarios = [
    { value: "bom", label: "Bom", bg: "bg-[#CAF0D3]", text: "text-[#1B6E4F]", border: "border-[#1B6E4F]" },
    { value: "regular", label: "Regular", bg: "bg-[#FFFDE7]", text: "text-[#FBC02D]", border: "border-[#FBC02D]" },
    { value: "ruim", label: "Ruim", bg: "bg-[#FFF3E0]", text: "text-[#F57C00]", border: "border-[#F57C00]" },
    { value: "critico", label: "Crítico", bg: "bg-[#FFEBEE]", text: "text-[#D32F2F]", border: "border-[#D32F2F]" },
    { value: "morto", label: "Morto", bg: "bg-[#E8EAED]", text: "text-[#3F4A52]", border: "border-[#3F4A52]" },
  ];

  const niveisRisco = [
    { value: "baixo", label: "Baixo", bg: "bg-[#CAF0D3]", text: "text-[#1B6E4F]", border: "border-[#1B6E4F]" },
    { value: "medio", label: "Médio", bg: "bg-[#FFFDE7]", text: "text-[#FBC02D]", border: "border-[#FBC02D]" },
    { value: "alto", label: "Alto", bg: "bg-[#FFF3E0]", text: "text-[#F57C00]", border: "border-[#F57C00]" },
    { value: "critico", label: "Crítico", bg: "bg-[#FFEBEE]", text: "text-[#D32F2F]", border: "border-[#D32F2F]" },
  ];

  const progresso = Math.round(((passo + 1) / passos.length) * 100);

  return (
    <div className="px-4 pt-4 pb-20 max-w-lg mx-auto">
      {/* Step indicator */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center flex-shrink-0">
            <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
          </button>
          <div className="flex-1">
            <div className="h-2 bg-[#E8EAED] rounded-full overflow-hidden">
              <div className="h-full bg-[#0052CC] rounded-full transition-all duration-300" style={{ width: `${progresso}%` }} />
            </div>
          </div>
          <span className="text-xs font-medium text-[#3F4A52]/60 flex-shrink-0">{passo + 1}/{passos.length}</span>
        </div>
        <p className="text-sm font-heading font-bold text-[#3F4A52] ml-10">{passos[passo]}</p>
      </div>

      {/* Step 0: Buscar */}
      {passo === 0 && (
        <div className="space-y-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#3F4A52]/40" />
            <input
              type="text"
              value={busca}
              onChange={(e) => { setBusca(e.target.value); setMostrarResultados(true); }}
              onFocus={() => setMostrarResultados(true)}
              placeholder="Código CTA, espécie ou bairro..."
              className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#E8EAED] text-sm focus:outline-none focus:border-[#0052CC]"
            />
          </div>
          <div className="flex gap-2">
            <button className="flex-1 py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52] flex items-center justify-center gap-1.5">
              <i className="ri-qr-scan-line" /> QR Code
            </button>
            <button className="flex-1 py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52] flex items-center justify-center gap-1.5">
              <i className="ri-map-pin-line" /> Próximas
            </button>
          </div>

          {(mostrarResultados && arvoresFiltradas.length > 0) && (
            <div className="space-y-2">
              <p className="text-xs text-[#3F4A52]/60">{arvoresFiltradas.length} encontradas</p>
              {arvoresFiltradas.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { setArvoreSelecionada(a); setPasso(1); setMostrarResultados(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E8EAED] bg-white text-left hover:border-[#0052CC] transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#E8EAED] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img src={a.foto} alt={a.especie} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#3F4A52] truncate">{a.especie}</p>
                    <p className="text-xs text-[#3F4A52]/50 font-mono">{a.id}</p>
                    <p className="text-xs text-[#3F4A52]/50">{a.bairro}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-[#3F4A52]/40" />
                </button>
              ))}
            </div>
          )}

          {busca.length > 1 && arvoresFiltradas.length === 0 && (
            <div className="text-center py-8">
              <i className="ri-search-line text-3xl text-[#3F4A52]/20" />
              <p className="text-sm text-[#3F4A52]/40 mt-2">Nenhuma árvore encontrada</p>
            </div>
          )}
        </div>
      )}

      {/* Step 1: Confirmar */}
      {passo === 1 && arvoreSelecionada && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E8EAED] rounded-xl overflow-hidden">
            <div className="h-40 bg-[#E8EAED] flex items-center justify-center overflow-hidden">
              <img src={arvoreSelecionada.foto} alt={arvoreSelecionada.especie} className="w-full h-full object-cover" />
            </div>
            <div className="p-4 space-y-3">
              <p className="text-lg font-heading font-bold text-[#3F4A52]">{arvoreSelecionada.especie}</p>
              <p className="text-xs font-mono text-[#0052CC]">{arvoreSelecionada.id}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-[#3F4A52]/60">Bairro:</span> <span className="font-medium text-[#3F4A52]">{arvoreSelecionada.bairro}</span></div>
                <div><span className="text-[#3F4A52]/60">Risco atual:</span> <span className="font-medium text-[#3F4A52]">{arvoreSelecionada.risco}</span></div>
                <div><span className="text-[#3F4A52]/60">Fitossanidade:</span> <span className="font-medium text-[#3F4A52]">{arvoreSelecionada.fitossanitario}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Medidas */}
      {passo === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">DAP (cm)</label>
              <input type="number" value={medidas.dap} onChange={(e) => setMedidas({ ...medidas, dap: e.target.value })} className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-center text-lg font-heading font-bold focus:outline-none focus:border-[#0052CC]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3F4A52] mb-1.5">Altura (m)</label>
              <input type="number" value={medidas.altura} onChange={(e) => setMedidas({ ...medidas, altura: e.target.value })} className="w-full px-3 py-3 rounded-xl border border-[#E8EAED] text-center text-lg font-heading font-bold focus:outline-none focus:border-[#0052CC]" />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Fitossanidade */}
      {passo === 3 && (
        <div className="space-y-4">
          {estadosFitossanitarios.map((e) => {
            const sel = fitossanitario === e.value;
            return (
              <button key={e.value} onClick={() => setFitossanitario(e.value)}
                className={`w-full py-4 rounded-xl border-2 text-sm font-heading font-bold ${sel ? `${e.border} ${e.bg} ${e.text}` : "border-[#E8EAED] bg-white text-[#3F4A52]"}`}>
                {e.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Step 4: Risco */}
      {passo === 4 && (
        <div className="space-y-4">
          {niveisRisco.map((r) => {
            const sel = risco === r.value;
            return (
              <button key={r.value} onClick={() => setRisco(r.value)}
                className={`w-full py-4 rounded-xl border-2 text-sm font-heading font-bold ${sel ? `${r.border} ${r.bg} ${r.text}` : "border-[#E8EAED] bg-white text-[#3F4A52]"}`}>
                {r.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Step 5: Manejo */}
      {passo === 5 && (
        <div className="space-y-2">
          {tiposManejo.map((m) => {
            const sel = manejo === m;
            return (
              <button key={m} onClick={() => setManejo(m)}
                className={`w-full py-3.5 px-4 rounded-xl border text-sm text-left transition-all ${
                  sel ? "border-[#1B6E4F] bg-[#CAF0D3] text-[#1B6E4F] font-medium" : "border-[#E8EAED] bg-white text-[#3F4A52]"
                }`}>
                {m}
              </button>
            );
          })}
        </div>
      )}

      {/* Step 6: Salvar */}
      {passo === 6 && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E8EAED] rounded-xl divide-y">
            {arvoreSelecionada && [
              ["Árvore", arvoreSelecionada.especie],
              ["Código", arvoreSelecionada.id],
              ["DAP", medidas.dap ? `${medidas.dap} cm` : "—"],
              ["Altura", medidas.altura ? `${medidas.altura} m` : "—"],
              ["Fitossanitário", fitossanitario || "—"],
              ["Risco", risco || "—"],
              ["Manejo", manejo || "—"],
            ].map(([label, value], i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <span className="text-xs text-[#3F4A52]/60">{label}</span>
                <span className="text-xs font-medium text-[#3F4A52]">{value}</span>
              </div>
            ))}
          </div>
          <button onClick={onClose} className="w-full py-4 rounded-xl bg-[#0052CC] text-white font-heading font-bold text-sm active:scale-[0.98]">
            <i className="ri-save-line mr-1.5" /> Salvar vistoria
          </button>
        </div>
      )}

      {/* Navigation */}
      {passo < 6 && (
        <div className="flex gap-3 mt-6">
          {passo > 0 && (
            <button onClick={() => setPasso(passo - 1)} className="flex-1 py-3.5 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52]">
              Voltar
            </button>
          )}
          <button
            onClick={() => setPasso(passo + 1)}
            disabled={(passo === 0 && !arvoreSelecionada)}
            className={`flex-1 py-3.5 rounded-xl text-white font-heading font-bold text-sm active:scale-[0.98] transition-transform ${
              (passo === 0 && !arvoreSelecionada) ? "bg-[#E8EAED] text-[#3F4A52]/40" : "bg-[#0052CC]"
            }`}
          >
            Próximo
          </button>
        </div>
      )}
    </div>
  );
}